import type { ProductBrief } from "../types/product";

export const productBrief: ProductBrief = {
  brand: "Sales Team Practice Ready",
  fullTitle: "Sales Team Practice Ready — 10 Ready-to-Run Sales Conversation Workshops",
  positioning: {
    headline: "Your next 10 sales training sessions. Already planned for you.",
    lead: "Ten complete 30-minute session plans with timed agendas, facilitator scripts, role-play cases and feedback sheets.",
    buyer:
      "Sales team leaders and small service-business owners coaching 3–12 sellers.",
    teaches: "Practice of customer sales conversations.",
    notFor: ["Manager leadership training", "Customer-service complaint handling"],
    promise: "Ten sales training sessions planned from opening to follow-up, with the materials already written.",
    doesNotPromise: [
      "Revenue outcomes",
      "Guaranteed conversions",
      "Certification",
      "Zero preparation",
    ],
  },
  price: {
    amountUsd: 27,
    currency: "USD",
    billing: "one-time",
    subscription: false,
  },
  coreManual: {
    volumes: [
      { number: 1, label: "Volume 1", workshops: "Workshops 1–5" },
      { number: 2, label: "Volume 2", workshops: "Workshops 6–10" },
    ],
    note: "Both volumes belong to the same $27 core kit. They are not separate purchases.",
  },
  workshopContents: [
    "30-minute facilitator plan with wording",
    "Two fictional cases (Case A and Case B)",
    "Seller, buyer and observer cards",
    "Model responses",
    "Behavioral observation sheet",
    "Follow-through application action",
  ],
  caseNote:
    "Case A is used in the timed session. Case B is an alternative or a later repeat — not extra work squeezed into the same 30 minutes.",
  format: {
    language: "English",
    deliverables: ["Facilitator manual PDFs (two volumes)", "Printable participant pages (PDF)"],
    notIncluded: [
      "Video",
      "Slides",
      "Editable templates",
      "Software",
      "Certification",
      "Resale rights",
    ],
    usage: "Internal-team use only.",
  },
  status: "Store preview — product files and payment setup are being completed.",
  sessionFlow: [
    { label: "Opening", minutes: 3 },
    { label: "Demonstration", minutes: 5 },
    { label: "Role rotations", minutes: 15 },
    { label: "Debrief", minutes: 5 },
    { label: "Application", minutes: 2 },
  ],
  workshops: [
    { number: 1, volume: 1, title: "Opening a sales conversation", outcome: "Set a purpose and start clearly." },
    { number: 2, volume: 1, title: "Discovery questions", outcome: "Uncover needs before pitching." },
    { number: 3, volume: 1, title: "Identifying fit", outcome: "Recognize when the service is appropriate." },
    { number: 4, volume: 1, title: "Explaining value", outcome: "Connect the proposal to stated needs." },
    { number: 5, volume: 1, title: "Price conversations", outcome: "Clarify \"it's too expensive\" before responding." },
    { number: 6, volume: 2, title: "Comparing alternatives", outcome: "Discuss verifiable differences." },
    { number: 7, volume: 2, title: "\"Send me some information\"", outcome: "Agree on useful information and a next step." },
    { number: 8, volume: 2, title: "Understanding the decision", outcome: "Identify people, criteria and timing." },
    { number: 9, volume: 2, title: "Useful follow-up", outcome: "Follow up with a reason and respect the response." },
    { number: 10, volume: 2, title: "The complete conversation", outcome: "Integrate the skills in guided practice." },
  ],
  bonuses: [
    {
      number: 1,
      title: "The 10-Week Sales Practice Calendar",
      description:
        "Schedules the ten workshops across ten weeks, with short follow-through prompts between sessions.",
      scopeNote: "Plans the core ten — no extra workshops",
    },
    {
      number: 2,
      title: "30 Five-Minute Sales Practice Huddles",
      description:
        "Thirty complete short activities to reinforce a skill at the start of a team meeting.",
      scopeNote: "Short reinforcements, not full workshops",
    },
    {
      number: 3,
      title: "The Sales Practice Needs & Skill Check Kit",
      description:
        "Helps you choose the next workshop from what you observe in real conversations.",
      scopeNote: "A practical guide, not a validated test",
    },
    {
      number: 4,
      title: "The Sales Practice Invitation & Reminder Pack",
      description:
        "Written invitations and reminders for your sessions. Add the date and location, then send.",
      scopeNote: "Only dates and location to fill in",
    },
    {
      number: 5,
      title: "The Sales Practice Facilitator Rescue Kit",
      description:
        "Responses and fallback activities for moments when practice feels awkward or stalls.",
      scopeNote: "For awkward or stalled sessions",
    },
  ],
  optionalExtensions: [
    {
      title: "Advanced Sales Conversation Practice Lab",
      priceUsd: 29,
      billing: "one-time",
      description: "Six additional advanced sessions.",
      includedInCoreKit: false,
    },
    {
      title: "The 12-Month Sales Practice Program",
      priceUsd: 37,
      billing: "one-time",
      description: "Twelve new workshops delivered together. Not a subscription.",
      includedInCoreKit: false,
    },
  ],
};
